import React, { createContext, useState, useContext, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import ICategory from '~/interfaces/ICategory';
import IComplement from '~/interfaces/IComplement';

interface CategoriesContextData {
  categories: ICategory[];
  complements: IComplement[];
  loadCategories(id: string): Promise<void>;
  loadComplements(id: string): Promise<void>;
  refreshComplements(id: string): Promise<void>;
  clearContext(): void;
  isInitialized: boolean;
}

const ProductsContext = createContext<CategoriesContextData>(
  {} as CategoriesContextData,
);

export const CategoriesProvider: React.FC = ({ children }) => {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [complements, setComplements] = useState<IComplement[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setCategories([]);
    setComplements([]);
  }, []);

  const loadComplements = useCallback(async (id: string) => {
    const q = query(
      collection(DB, 'complements'),
      where('company_id', '==', id),
      where('status', '==', true),
    );
    const querySnapshot = await getDocs(q);

    const items: IComplement[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IComplement;

      items.push(parseItem);
    });

    setComplements(items);
    setIsInitialized(true);
  }, []);

  const refreshComplements = useCallback(async (id: string) => {
    const q = query(
      collection(DB, 'complements'),
      where('company_id', '==', id),
      where('status', '==', true),
    );
    const querySnapshot = await getDocs(q);

    const items: IComplement[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IComplement;

      items.push(parseItem);
    });

    setComplements(items);
  }, []);

  const loadCategories = useCallback(async (id: string) => {
    const categoriesRef = collection(DB, 'categories');
    const q = query(
      categoriesRef,
      where('company_id', '==', id),
      where('status', '==', true),
    );
    const querySnapshot = await getDocs(q);

    const items: ICategory[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as ICategory;

      items.push(parseItem);
    });

    items.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));

    setCategories(items);
    setIsInitialized(true);
  }, []);

  return (
    <ProductsContext.Provider
      value={{
        categories,
        loadCategories,
        complements,
        loadComplements,
        refreshComplements,
        clearContext,
        isInitialized,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export function useCategories(): CategoriesContextData {
  const context = useContext(ProductsContext);
  return context;
}
